import { useCallback, useEffect, useMemo, useState } from 'react';
import { getReaderChapterVoice, ReaderBook, ReaderChapter } from '@/services/booksApi';

const isAudioNavigableChapter = (chapter: ReaderChapter, voiceKey: string): boolean =>
  Boolean(getReaderChapterVoice(chapter, voiceKey)?.audioUrl);

interface InitialChapterProgress {
  chapterId?: string;
  timeOffset?: number;
  totalProgress?: number;
}

interface InitialChapterResolution {
  index: number;
  recoveryNotice: string | null;
}

export const resolveInitialChapter = (
  chapters: ReaderChapter[],
  voiceKey: string,
  progress?: InitialChapterProgress | null
): InitialChapterResolution => {
  const playableIndices = chapters
    .map((chapter, index) => isAudioNavigableChapter(chapter, voiceKey) ? index : -1)
    .filter((index) => index >= 0);
  const firstPlayableIndex = playableIndices[0] ?? -1;
  const fallbackIndex = firstPlayableIndex >= 0 ? firstPlayableIndex : 0;
  if (!progress?.chapterId) {
    return { index: fallbackIndex, recoveryNotice: null };
  }

  const manifestSavedIndex = chapters.findIndex((chapter) => chapter.id === progress.chapterId);
  const savedIndex = manifestSavedIndex >= 0 && isAudioNavigableChapter(chapters[manifestSavedIndex], voiceKey)
    ? manifestSavedIndex
    : -1;
  if (savedIndex < 0) {
    let recoveredIndex = fallbackIndex;
    if (manifestSavedIndex >= 0 && playableIndices.length > 0) {
      recoveredIndex = playableIndices.reduce((best, candidate) => {
        const candidateDistance = Math.abs(candidate - manifestSavedIndex);
        const bestDistance = Math.abs(best - manifestSavedIndex);
        return candidateDistance < bestDistance || (candidateDistance === bestDistance && candidate > best)
          ? candidate
          : best;
      }, playableIndices[0]);
    } else if (Number.isFinite(progress.totalProgress) && playableIndices.length > 0) {
      let elapsed = 0;
      const starts = chapters.map((chapter) => {
        const start = elapsed;
        elapsed += (getReaderChapterVoice(chapter, voiceKey)?.durationMs ?? 0) / 1000;
        return start;
      });
      recoveredIndex = playableIndices.reduce((best, candidate) => {
        const candidateDistance = Math.abs(starts[candidate] - (progress.totalProgress ?? 0));
        const bestDistance = Math.abs(starts[best] - (progress.totalProgress ?? 0));
        return candidateDistance < bestDistance || (candidateDistance === bestDistance && candidate > best)
          ? candidate
          : best;
      }, playableIndices[0]);
    }
    return {
      index: recoveredIndex,
      recoveryNotice: playableIndices.length > 0
        ? `Your saved chapter is no longer playable with this reading voice. Resumed at ${chapters[recoveredIndex]?.title ?? 'the nearest available chapter'}.`
        : 'Your saved chapter is no longer playable and this book has no available narration.',
    };
  }

  const savedChapter = chapters[savedIndex];
  const savedChapterDuration = (getReaderChapterVoice(savedChapter, voiceKey)?.durationMs || 0) / 1000;
  const isFinishedFinalChapter =
    savedIndex === chapters.length - 1 &&
    savedChapterDuration > 0 &&
    (progress.timeOffset || 0) > 0 &&
    (progress.timeOffset || 0) >= Math.max(0, savedChapterDuration - 2);

  return {
    index: isFinishedFinalChapter ? fallbackIndex : savedIndex,
    recoveryNotice: null,
  };
};

export const useChapterManager = (
  book: ReaderBook | null,
  voiceKey: string,
  initialProgress?: InitialChapterProgress | null,
) => {
  const chapters = book?.chapters ?? [];
  const initialResolution = useMemo(
    () => resolveInitialChapter(chapters, voiceKey, initialProgress),
    [chapters, initialProgress, voiceKey]
  );
  const [selection, setSelection] = useState<{ bookId: string | null; index: number; recoveryNotice: string | null }>({
    bookId: null,
    index: 0,
    recoveryNotice: null,
  });
  const currentChapterIndex = selection.bookId === book?.id
    ? selection.index
    : initialResolution.index;
  const chapterRecoveryNotice = selection.bookId === book?.id
    ? selection.recoveryNotice
    : initialResolution.recoveryNotice;
  const setCurrentChapterIndex = useCallback((index: number) => {
    setSelection({ bookId: book?.id ?? null, index, recoveryNotice: null });
  }, [book?.id]);

  useEffect(() => {
    setSelection((current) => {
      const nextBookId = book?.id ?? null;
      if (current.bookId === nextBookId) {
        return current;
      }
      return { ...initialResolution, bookId: nextBookId };
    });
  }, [book?.id, initialResolution]);

  const currentChapter = useMemo(() => {
    if (chapters.length === 0) {
      return null;
    }
    if (currentChapterIndex < 0 || currentChapterIndex >= chapters.length) {
      return null;
    }
    return chapters[currentChapterIndex] ?? null;
  }, [chapters, currentChapterIndex]);

  const findNextPlayableIndex = useCallback(
    (startIndex: number): number => {
      for (let i = startIndex + 1; i < chapters.length; i += 1) {
        if (isAudioNavigableChapter(chapters[i], voiceKey)) {
          return i;
        }
      }
      return -1;
    },
    [chapters, voiceKey]
  );

  const findPreviousPlayableIndex = useCallback(
    (startIndex: number): number => {
      for (let i = startIndex - 1; i >= 0; i -= 1) {
        if (isAudioNavigableChapter(chapters[i], voiceKey)) {
          return i;
        }
      }
      return -1;
    },
    [chapters, voiceKey]
  );

  const nextChapter = useCallback((): boolean => {
    if (chapters.length === 0) {
      return false;
    }

    const nextIndex = findNextPlayableIndex(currentChapterIndex);
    if (nextIndex < 0) {
      return false;
    }

    setCurrentChapterIndex(nextIndex);
    return true;
  }, [chapters.length, currentChapterIndex, findNextPlayableIndex]);

  const previousChapter = useCallback((): boolean => {
    if (chapters.length === 0) {
      return false;
    }

    const previousIndex = findPreviousPlayableIndex(currentChapterIndex);
    if (previousIndex < 0) {
      return false;
    }

    setCurrentChapterIndex(previousIndex);
    return true;
  }, [chapters.length, currentChapterIndex, findPreviousPlayableIndex]);

  const goToChapter = useCallback(
    (index: number): boolean => {
      if (index < 0 || index >= chapters.length) {
        return false;
      }
      setCurrentChapterIndex(index);
      return true;
    },
    [chapters.length]
  );

  const hasNextChapter = useMemo(() => {
    return findNextPlayableIndex(currentChapterIndex) >= 0;
  }, [currentChapterIndex, findNextPlayableIndex]);

  const hasPreviousChapter = useMemo(() => {
    return findPreviousPlayableIndex(currentChapterIndex) >= 0;
  }, [currentChapterIndex, findPreviousPlayableIndex]);

  return {
    chapters,
    currentChapter,
    currentChapterIndex,
    setCurrentChapterIndex,
    nextChapter,
    previousChapter,
    goToChapter,
    hasNextChapter,
    hasPreviousChapter,
    chapterRecoveryNotice,
  };
};
